import { useEffect, useState } from "react";
import { useValidator } from "@common/hooks/validation";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { post, setJwtToken } from "@common/api/apiShared";
import { useUrlProvider } from "../../useUrlProvider";
import { useNavigate } from "react-router";
import { StandardLegalEntityType } from "../LegalEntity.types";
import { useLegalEntityTypeNames } from "../useLegalEntityTypeNames";
import { LegalEntityScope } from "../legalEntities/LegalEntities.types";
import { CvrResultViewModel } from "../cvrSelector/CvrSelector.types";
import { useDotLegalMutation, useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useSelectableEntityTemplatesForCreation } from "@common/hooks/useSelectableItemQueries";
import { EntityTemplate, LegalEntityStandardField, TemplateEntityType } from "../../entityTemplates/EntityTemplate.types";
import { hasFieldData } from "../../entityTemplates/fieldValue";
import { LegalEntityFieldModel } from "../../entityTemplates/LegalEntityFieldRenderers";
import { getFieldName, getLegalEntityFieldValue } from "../editLegalEntity/EditLegalEntityValidator.hooks";
import { ValidationError } from "@common/validationError";
import { usePlanContext } from "../../auth/planProvider/PlanProvider";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { LegalEntitySaveModel } from "../addLegalEntityDialog/AddLegalEntityDialog.types";
import { groupFieldsByTemplateStep } from "../../entityTemplates/templateStepFields/groupFieldsByTemplateStep";
import { usePane } from "../../main/panes/paneLayout/usePane";
import { AddLegalEntityPaneProps } from "./AddLegalEntityPane.types";

// Name and country live on the save model, where the pane validates them before anything else, so the
// required-at-creation check must not look for them on the edited entity.
const saveModelFields: Set<number> = new Set([LegalEntityStandardField.LegalEntityName, LegalEntityStandardField.LegalEntityCountry]);

function createEmptyRequiredFieldValues(isApproved: boolean): LegalEntityFieldModel {
    return {
        id: "",
        types: [],
        isApproved: isApproved,
        isActive: true,
        isDataProcessorOnTransfers: false,
        isSubDataProcessorOnTransfers: false,
        businessAreaIds: [],
        complianceAreaIds: [],
        certificationIds: [],
        subContractorIds: [],
        shouldAutoAddSubContractors: false,
        customFieldValues: [],
    };
}

function getCvrFieldValues(cvrResult: CvrResultViewModel | undefined): Partial<LegalEntityFieldModel> {
    return {
        address: cvrResult?.address,
        city: cvrResult?.city,
        zipCode: cvrResult?.zipCode,
        companyRegistrationNumber: cvrResult?.companyRegistrationNumber,
        website: cvrResult?.website,
        description: cvrResult?.description,
    };
}

export function useAddLegalEntityPane(props: AddLegalEntityPaneProps) {
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();
    const { getLegalEntityEditUrl } = useUrlProvider();
    const navigate = useNavigate();
    const { platformFeatures } = usePlanContext();
    const { permissions } = useUserContext();
    const { close, setHasUnsavedChanges } = usePane();

    const isGroupEntityCreation = props.legalEntityScope === LegalEntityScope.GroupEntity;
    const isExternalCreation = props.legalEntityScope === LegalEntityScope.External;
    // The group entity list is gated on its own permission, so creating there is a full creation for a user
    // who may manage group entities but not companies.
    const canCreateCompany = isGroupEntityCreation ? permissions.canManageGroupEntities : permissions.companyPermissions.create;

    const { getStandardTypeId } = useLegalEntityTypeNames();

    const [saveModel, setSaveModel] = useState<LegalEntitySaveModel>(getDefaultSaveModel());
    const [selectedCvrResult, setSelectedCvrResult] = useState<CvrResultViewModel | undefined>(undefined);
    const [editedLegalEntity, setEditedLegalEntity] = useState<LegalEntityFieldModel>(createEmptyRequiredFieldValues(canCreateCompany));
    const [selectedTagIds, setSelectedTagIds] = useState<Array<string>>([]);
    const [showRequiredFieldErrors, setShowRequiredFieldErrors] = useState(false);

    // The name and country are the only fields the CVR lookup disables.
    const disableFields = selectedCvrResult !== undefined;

    const { data: entityTemplatesData, isLoading: templatesIsLoading } = useSelectableEntityTemplatesForCreation(TemplateEntityType.LegalEntity, false);

    const templates = entityTemplatesData?.map((x) => ({ id: x.id, name: x.name })) ?? [];

    const selectedTemplate = entityTemplatesData?.find((t) => t.id === saveModel.entityTemplateId);
    // The full template carries no such flag: the selectable view model is what reports that the user is
    // barred from a field this template insists on at creation time.
    const selectedTemplateMissingAccess = selectedTemplate?.missingRequiredAtCreationFieldAccess ?? false;

    const templateQuery = useDotLegalQuery<EntityTemplate>(`/entity-templates/${saveModel.entityTemplateId}`, { enabled: !!saveModel.entityTemplateId });
    const template = templateQuery.data;

    const tagsAvailable = platformFeatures.tags && permissions.canAccessTags;

    // A user who may only create unapproved entities never approves them, so promoting the field would render a
    // checkbox they cannot act on - and one hasFieldData reads as empty, which would block creation entirely.
    const isFieldCollectedAtCreation = (standardField?: number | null) =>
        standardField == null ||
        ((canCreateCompany || standardField !== LegalEntityStandardField.LegalEntityApproved) &&
            (tagsAvailable || standardField !== LegalEntityStandardField.LegalEntityTags));

    const requiredAtCreationFields = (template?.steps.flatMap((step) => step.fields) ?? []).filter(
        (field) => field.requiredAtCreation && isFieldCollectedAtCreation(field.standardFieldIdentifier)
    );

    const fieldGroups = groupFieldsByTemplateStep(template, requiredAtCreationFields);

    const validation = useValidator<LegalEntitySaveModel>(
        (validators) => ({
            name: validators.validateNotEmpty((item) => item.name, translateString("name")),
            countryCode: validators.validateNotEmpty((item) => item.countryCode, translateString("country")),
            entityTemplateId: validators.validateNotEmpty((item) => item.entityTemplateId, translateString("legalEntityTemplate")),
        }),
        [saveModel?.countryCode, saveModel?.name, saveModel?.entityTemplateId]
    );

    validation.check(saveModel);

    useEffect(() => {
        if (entityTemplatesData?.length === 1 && !saveModel.entityTemplateId) {
            selectTemplate(entityTemplatesData[0].id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityTemplatesData, saveModel.entityTemplateId]);

    const validateRequiredAtCreationFields = (): Array<ValidationError> => {
        const errors: Array<ValidationError> = [];
        const requiredTranslation = translateString("requiredField");

        for (const field of requiredAtCreationFields) {
            if (field.standardFieldIdentifier && saveModelFields.has(field.standardFieldIdentifier)) {
                continue;
            }

            if (!hasFieldData(getLegalEntityFieldValue(field, editedLegalEntity, selectedTagIds))) {
                errors.push(new ValidationError(getFieldName(field), requiredTranslation));
            }
        }

        return errors;
    };

    const requiredFieldValidationErrors = showRequiredFieldErrors ? validateRequiredAtCreationFields() : [];

    const saveMutation = useDotLegalMutation(save);

    const onSaveClick = () => {
        const requiredFieldErrors = validateRequiredAtCreationFields();

        if (validation.anyHasErrors) {
            validation.setShowErrors(true);
        }

        if (requiredFieldErrors.length > 0) {
            setShowRequiredFieldErrors(true);
        }

        if (validation.anyHasErrors || requiredFieldErrors.length > 0) {
            return;
        }

        saveMutation.mutateAsync(undefined, {
            onSuccess: (response: any) => {
                const creationResult = response.value();

                if (!creationResult.isCreated) {
                    snackbar.show(translateString("addEntityDataExist"), "warning");
                    return;
                }

                if (creationResult.refetchJwtToken) {
                    setJwtToken("");
                }

                // A pane outlives a navigation, so it has to close itself or it stays open over the new entity.
                // The wizard opens on a brand new entity, so the steps must not flag missing required fields
                // before the user has had a chance to reach them.
                close();
                navigate(getLegalEntityEditUrl(props.legalEntityScope, creationResult.id, "create=true"));
            },
        });
    };

    function selectTemplate(entityTemplateId: string | null) {
        setSaveModel({ ...saveModel, entityTemplateId: entityTemplateId ?? "" });
        setEditedLegalEntity({ ...createEmptyRequiredFieldValues(canCreateCompany), ...getCvrFieldValues(selectedCvrResult) });
        setSelectedTagIds([]);
        setShowRequiredFieldErrors(false);
    }

    function onTemplateChange(entityTemplateId: string | null) {
        setHasUnsavedChanges(true);
        selectTemplate(entityTemplateId);
    }

    async function onRequiredFieldChange(changeFunc: (legalEntity: LegalEntityFieldModel) => void) {
        setHasUnsavedChanges(true);
        setEditedLegalEntity((current) => {
            const next = { ...current };
            changeFunc(next);
            return next;
        });
    }

    function onTagsChange(tagIds: Array<string>) {
        setHasUnsavedChanges(true);
        setSelectedTagIds(tagIds);
    }

    const onSaveModelChange = {
        onNameChange: (name: string) => {
            setHasUnsavedChanges(true);
            setSaveModel((current) => ({ ...current, name }));
        },
        onCountryChange: (countryCode: string) => {
            setHasUnsavedChanges(true);
            setSaveModel((current) => ({ ...current, countryCode }));
        },
    };

    function onCvrLegalEntitySelected(legalEntity: CvrResultViewModel) {
        setHasUnsavedChanges(true);
        setSaveModel((current) => ({ ...current, name: legalEntity.name, countryCode: "DK" }));
        setEditedLegalEntity((current) => ({ ...current, ...getCvrFieldValues(legalEntity) }));
        setSelectedCvrResult(legalEntity);
    }

    function onCvrLegalEntityRemoved() {
        // Clearing the company must not undo the template choice or the required field values entered under it.
        setSaveModel({ ...getDefaultSaveModel(), entityTemplateId: saveModel.entityTemplateId });
        setEditedLegalEntity((current) => ({ ...current, ...getCvrFieldValues(undefined) }));
        setSelectedCvrResult(undefined);
    }

    return {
        saveModel,
        onSaveClick,
        isSaving: saveMutation.isLoading,
        validation,
        onSaveModelChange,
        onCvrLegalEntitySelected,
        onCvrLegalEntityRemoved,
        selectedCvrResult,
        templates,
        templatesIsLoading,
        isLoadingTemplate: templateQuery.isLoading && !!saveModel.entityTemplateId,
        onTemplateChange,
        selectedTemplateMissingAccess,
        fieldGroups,
        selectedTagIds,
        onTagsChange,
        editedLegalEntity,
        disableFields,
        onRequiredFieldChange,
        requiredFieldValidationErrors,
    };

    // The external endpoint is the one a role without create rights on the entity being created may reach, and
    // it is what pins the entity to unapproved. Creating with those rights is still an ordinary creation.
    function save() {
        return post<{}>(canCreateCompany ? "/legal-entities" : "/legal-entities/external", mapToSaveModel());
    }

    function mapToSaveModel() {
        return {
            ...saveModel,
            type: saveModel.type ?? (isExternalCreation ? getStandardTypeId(StandardLegalEntityType.Vendor) : undefined),
            entityTemplateId: saveModel.entityTemplateId || null,
            customFieldValues: editedLegalEntity.customFieldValues,
            note: editedLegalEntity.note,
            referenceNumber: editedLegalEntity.referenceNumber,
            description: editedLegalEntity.description,
            address: editedLegalEntity.address,
            zipCode: editedLegalEntity.zipCode,
            city: editedLegalEntity.city,
            companyRegistrationNumber: editedLegalEntity.companyRegistrationNumber,
            website: editedLegalEntity.website,
            types: editedLegalEntity.types,
            isApproved: editedLegalEntity.isApproved,
            isActive: editedLegalEntity.isActive,
            responsibleId: editedLegalEntity.responsibleId,
            businessAreaIds: editedLegalEntity.businessAreaIds,
            complianceAreaIds: editedLegalEntity.complianceAreaIds,
            certificationIds: editedLegalEntity.certificationIds,
            auditType: editedLegalEntity.auditType,
            auditFrequency: editedLegalEntity.auditFrequency,
            tagIds: selectedTagIds,
            subContractorIds: editedLegalEntity.subContractorIds,
        };
    }

    function getDefaultSaveModel(): LegalEntitySaveModel {
        return {
            name: "",
            countryCode: "",
            isCustomerOwned: isGroupEntityCreation,
            // Creating from the external list seeds the vendor type, which is what the overwhelming majority of
            // external entities are. The types are master data, so the id is not known until they have loaded
            // and the seed is resolved when the save payload is built. A group entity is defined by ownership.
            type: undefined,
            entityTemplateId: "",
        };
    }
}
